import { FC } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CaseReportsAPI from "@/api/cases/reports";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { CaseReportsQuery } from "../caseReports.query";

interface CaseReportRowData {
  case_report_id: number;
  name: string;
}

interface DeleteCaseReportModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  caseId: string | number;
  defaultInfo: CaseReportRowData;
}

export const DeleteCaseReportModal: FC<DeleteCaseReportModalProps> = ({
  show,
  onClose,
  onSubmit,
  caseId,
  defaultInfo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (params: {
      case_id: string | number;
      case_report_id: number;
    }) => CaseReportsAPI.deleteCaseReport(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CaseReportsQuery.queryKeys.base,
      });

      enqueueSnackbar(`Case Report "${defaultInfo.name}" deleted.`, {
        variant: "success",
      });

      LoggingAPI.createLog({
        case_id: caseId,
        user_id: currentUser.user_id,
        message: `Deleted case report: ${defaultInfo.name}.`,
        event: "delete_case_report",
        object_type: "case_report",
      });

      onSubmit?.();
      onClose();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while deleting this case report. Please try again.",
        { variant: "error" }
      );
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate({
      case_id: caseId,
      case_report_id: defaultInfo.case_report_id,
    });
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete Case Report</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Are you sure you want to delete case report{" "}
        <span style={{ fontWeight: 800 }}>"{defaultInfo.name}"</span>?
        <p>This action cannot be undone.</p>
      </div>
      <ButtonMenu>
        <Button variant="subtle" size="xs" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          size="xs"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          Delete Case Report
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
